import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fmtDate } from '@dcc-erp/shared';
import type { ApBillApprovalRow, ApPaymentWithAllocations } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApPayment,
  useApproveApPaymentStep,
  useRejectApPayment,
  useReturnApPayment,
  useSubmitApPayment,
} from '../../lib/queries/ap';
import { AppModal } from '../AppModal';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

interface Props {
  companyId: string;
  payment: ApPaymentWithAllocations;
  onClose: () => void;
}

// Mask a destination account number to its last 4 digits for the read-only
// snapshot line ('••••1234').
function maskDestAccount(acct: string | null | undefined): string {
  const digits = (acct ?? '').replace(/\D/g, '');
  if (!digits) return '—';
  return `••••${digits.slice(-4)}`;
}

// ApPaymentApprovalPanel — single-bill payment engine 4-eyes (docType APV). The
// maker submits (appayment.create); the finance approver pool decides
// (appayment.approve). On final approve the server posts the Dr AP_TRADE / Cr cash
// JV (journalEntryId set) — the panel just invalidates so the live row refreshes.
export function ApPaymentApprovalPanel({ companyId, payment, onClose }: Props) {
  const { data: detail } = useApPayment(companyId, payment.id);
  const view = detail ?? payment;

  const submitMut = useSubmitApPayment(companyId);
  const approveMut = useApproveApPaymentStep(companyId);
  const rejectMut = useRejectApPayment(companyId);
  const returnMut = useReturnApPayment(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  const approvalStatus = view.approvalStatus ?? null;
  const currentStep = view.currentStep ?? 0;
  // M7 Phase 2 (D5) — destination-bank SNAPSHOT, frozen at DRAFT create. Surfaced
  // read-only so the pay-to-the-snapshot invariant is discoverable (a later VBC
  // bank change must NOT redirect this in-flight payment).
  const hasDestBank = !!view.destBankAccountNumber;
  // The detail (ApPaymentWithDetail) carries the materialized chain; the
  // header-only fallback (ApPaymentWithAllocations) does not, so default to [].
  const approvals: ApBillApprovalRow[] =
    'approvals' in view && Array.isArray(view.approvals) ? view.approvals : [];
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(view.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          id: view.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['ap-payments', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <AppModal
      title={`อนุมัติการจ่าย — ${view.voucherNo}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="appayment.create">
              <button
                className="btn primary"
                disabled={submitBusy}
                data-testid="payment-submit"
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="appayment.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                data-testid="payment-decide"
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </>
      }
    >
      {approvalStatus === 'RETURNED' && view.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
          data-testid="payment-returned-reason"
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {view.returnedReason}
        </div>
      )}

      {isPending && (
        <div className="meta" style={{ marginBottom: 12 }}>
          <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
        </div>
      )}

      <ApprovalTimeline
        approvals={approvals}
        isActive={isPending}
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          view.submittedBy ? (
            <div className="meta">
              ส่งอนุมัติโดย {view.submittedBy}
              {view.submittedAt ? ` · ${fmtDate(view.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
        trailLine={
          approvalStatus === 'APPROVED' && view.approvedBy ? (
            <div className="meta">
              อนุมัติโดย {view.approvedBy}
              {view.approvedAt ? ` · ${fmtDate(view.approvedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      {hasDestBank && (
        <div
          style={{
            marginTop: 12,
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 13,
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap',
          }}
          data-testid="payment-dest-bank"
        >
          <div style={{ width: '100%' }}>
            <div className="meta">บัญชีปลายทาง (ขณะสร้างรายการ)</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 11 }}>
              เลขบัญชี
            </div>
            <div className="code">{maskDestAccount(view.destBankAccountNumber)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 11 }}>
              ธนาคาร
            </div>
            <div>{view.destBankCode ?? '—'}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 11 }}>
              ชื่อบัญชี
            </div>
            <div>{view.destBankAccountName ?? '—'}</div>
          </div>
        </div>
      )}

      {actionError && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 10 }}
          data-testid="payment-action-error"
        >
          {actionError}
        </div>
      )}

      {showDecide && (
        <DecisionModal
          voucherNo={view.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาการจ่าย"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </AppModal>
  );
}
